import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { openEmailComposer } from '#/shared/lib/email-composer'
import type { EmailComposerTarget } from '#/shared/lib/email-composer'
import { cn } from '#/shared/lib/utils'
import { CopyIcon, EmailIcon, SendIcon } from '#/shared/ui/icons'
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '#/shared/ui/tooltip'

function EmailActionButton({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className="flex cursor-pointer items-center gap-1.25 rounded px-1.5 py-1 font-medium text-white transition-colors hover:bg-white/15"
    >
      {icon}
      {label}
    </button>
  )
}

/** Copies an email address, briefly flipping the label to "Copied". */
function useCopyEmail(value: string) {
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      return
    }
    setCopied(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopied(false), 1500)
  }

  return { copied, copy }
}

interface CallableEmailProps {
  value: string
  className?: string
  /** Recipient details used to pre-fill the composer opened on click. */
  target: EmailComposerTarget
  /**
   * Hide the leading email icon while keeping the one-tap-compose behaviour and
   * the Email/Copy hover tooltip. Used where the surrounding layout already
   * conveys "email" and a second icon would be redundant.
   */
  hideIcon?: boolean
}

function EmailTooltipActions({
  target,
  copied,
  onEmail,
  onCopy,
}: {
  target: EmailComposerTarget
  copied: boolean
  onEmail: (target: EmailComposerTarget) => void
  onCopy: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <EmailActionButton
        icon={<SendIcon className="size-3.5" />}
        label={t('email.actions.email')}
        onClick={() => onEmail(target)}
      />
      <span aria-hidden className="h-4 w-px bg-white/20" />
      <EmailActionButton
        icon={<CopyIcon className="size-3.5" />}
        label={copied ? t('email.actions.copied') : t('email.actions.copy')}
        onClick={onCopy}
      />
    </>
  )
}

/** An email address that reveals quick actions on hover. Clicking the icon or
 *  the address opens the email composer pre-filled with the recipient; the
 *  tooltip offers "Email" (compose) and "Copy". Mirrors {@link CallablePhone}. */
export function CallableEmail({
  value,
  className,
  target,
  hideIcon,
}: CallableEmailProps) {
  const { t } = useTranslation()
  const { copied, copy } = useCopyEmail(value)
  // Controlled so we can force-close it when an action opens the composer —
  // Radix otherwise keeps the tooltip open since the pointer never leaves the
  // trigger before focus moves to the dialog.
  const [open, setOpen] = useState(false)
  const onCopy = () => void copy()
  const onEmail = (next: EmailComposerTarget) => {
    setOpen(false)
    openEmailComposer(next)
  }

  return (
    <TooltipProvider delayDuration={150}>
      <TooltipRoot open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <span className="group inline-flex min-w-0 items-center gap-1.25">
            {!hideIcon && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  onEmail(target)
                }}
                aria-label={t('email.emailAria', { address: value })}
                className="flex shrink-0 cursor-pointer items-center text-gray-500 transition-colors group-hover:text-primary"
              >
                <EmailIcon className="size-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                onEmail(target)
              }}
              aria-label={t('email.emailAria', { address: value })}
              className={cn(
                'min-w-0 cursor-pointer truncate text-left transition-colors select-none hover:text-blue-500',
                className,
              )}
            >
              {value}
            </button>
          </span>
        </TooltipTrigger>
        <TooltipContent
          // z-[80] (popout layer): float above the modal layer (z-70) and the
          // detail sidebar (z-60) so the actions stay visible when an email
          // field is rendered inside either. See app/styles/index.css.
          className="z-[80] flex items-center gap-0.5 p-1"
          showArrow
        >
          <EmailTooltipActions
            target={target}
            copied={copied}
            onEmail={onEmail}
            onCopy={onCopy}
          />
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}
